import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Button · T4L
 * Per Olga DESIGN-SYSTEM §5 + COMPONENT-LIBRARY §1
 *
 * Variants (5):
 *   primary     · main action · bg-brand-bright text-white hover darken
 *   secondary   · second action · bg-white border slate-200 hover bg-slate-50
 *   ghost       · toolbar action · bg-transparent text-slate-600 hover bg-slate-100
 *   destructive · destructive · bg-red-600 text-white hover bg-red-700 (alias: danger)
 *   danger      · soft destructive · bg-red-50 text-red-700 hover bg-red-100 (soft variant)
 *   icon        · icon-only · p-2 rounded-lg hover:bg-slate-100 text-slate-600
 *
 * Sizes (3):
 *   sm  · px-3 py-1.5 text-xs · inline · drobne toolbar
 *   md  · px-4 py-2 text-sm  · default · form/dialog/page action (DEFAULT)
 *   lg  · px-6 py-3 text-base · hero CTA · primary page action
 *
 * Acceptance per Olga:
 *   - min target 32×32 (a11y)
 *   - focus state ring-2 ring-brand-bright/30
 *   - active scale[0.98] momentary feedback
 *   - hover subtle darken 8-12% · NIE gradient shift
 *   - disabled opacity-50 cursor-not-allowed
 */

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'danger' | 'icon';
  size?: 'sm' | 'md' | 'lg';
}

const VARIANT_STYLES: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-[#239D9F] text-white hover:bg-[#1E8A8C] focus:ring-[#239D9F]/30 active:scale-[0.98]',
  secondary:
    'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 focus:ring-slate-300 active:scale-[0.98]',
  ghost:
    'bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900 focus:ring-slate-300',
  destructive:
    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 active:scale-[0.98]',
  // Backward compat alias for legacy 'danger' callers · matches destructive (hard)
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 active:scale-[0.98]',
  icon:
    'p-2 bg-transparent text-slate-600 hover:bg-slate-100 focus:ring-slate-300 rounded-lg',
};

const SIZE_STYLES: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-xs',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', ...props }, ref) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-all duration-200 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed';

    const sizeClass = variant === 'icon' ? '' : SIZE_STYLES[size];

    return (
      <button
        className={cn(baseStyles, VARIANT_STYLES[variant], sizeClass, className)}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';
export { Button };
export default Button;
